import React, { useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Modal,
  Button,
  Paper,
} from '@mui/material';
import { useApiSend, getApiErrorMessage } from '../../utils/httpClientTs';
import { createMessageModal } from '../../endpoints/messagesTs';
import {
  MessageCreateModalProps,
  MessageCreateRequest,
  MessageCreateResponse,
} from '../../types/messages';
import { LoadingButton } from '@mui/lab';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 650,
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
};

export default function MessageCreate({
  modalOpen,
  setModalOpen,
  id,
  name,
  toType,
  screenName,
}: MessageCreateModalProps) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutate: sendMessage } = useApiSend<
    MessageCreateResponse,
    MessageCreateRequest
  >(
    createMessageModal,
    (data) => {
      setIsSaving(false);
      setMessage('');
      setError(null);
      setModalOpen(false);
    },
    (error) => {
      setError(getApiErrorMessage(error));
      setIsSaving(false);
    }
  );

  const handleSendMessage = useCallback(() => {
    setError(null);

    // Validation
    if (!message.trim()) {
      setError('Message cannot be empty');
      return;
    }

    if (message.length > 2000) {
      setError('Message cannot be longer than 2000 characters');
      return;
    }

    setIsSaving(true);
    sendMessage({
      to_id: id,
      to_type: toType,
      name: name,
      message: message.trim(),
      screen_name: screenName,
    });
  }, [message, id, toType, name, screenName, sendMessage]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      setModalOpen(false);
      setMessage('');
      setError(null);
    }
  }, [isSaving, setModalOpen]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="create-message-modal"
      aria-describedby="create-message-description"
    >
      <Paper sx={modalStyle}>
        <Grid container>
          {/* Header */}
          <Grid
            item
            xs={12}
            sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h4" component="h2" textAlign="center">
              Create Message
            </Typography>
          </Grid>

          {/* Recipient Info */}
          <Grid item xs={12} sx={{ p: 3 }}>
            <Typography variant="body1">
              <strong>To:</strong> {name}
            </Typography>
          </Grid>

          {/* Message Input */}
          <Grid item xs={12} sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Message"
              variant="outlined"
              size="small"
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message here..."
              helperText={`${message.length}/2000 characters`}
              error={!!error}
              disabled={isSaving}
            />
          </Grid>

          {/* Guidelines */}
          <Grid item xs={12} sx={{ px: 3, pb: 2 }}>
            <Typography
              variant="caption"
              sx={{ fontStyle: 'italic', color: 'text.secondary' }}
            >
              Your message may be subject to review. Please ensure it adheres to
              our community guidelines. Press Ctrl+Enter to send.
            </Typography>
          </Grid>

          {/* Error Display */}
          {error && (
            <Grid item xs={12} sx={{ px: 3, pb: 2 }}>
              <Typography variant="body2" color="error" textAlign="center">
                {error}
              </Typography>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid
            item
            xs={12}
            sx={{
              p: 3,
              pt: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              loading={isSaving}
              disabled={isSaving || !message.trim()}
              onClick={handleSendMessage}
            >
              Send Message
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
